/**
 * CSV export
 *
 * Third consumer of the output render model (see renderModel.ts) alongside the
 * HTML and PDF renderers: the BOM columns — including retailer custom SKU
 * columns and saved column layouts — come from the same descriptor, so the
 * exported file can't drift from the on-screen Bill of Materials.
 *
 * Shape: a metadata block (project title, room configuration, estimate URL)
 * followed by ONE flat table — every BOM section's rows in on-screen order.
 * Line-item numbers run continuously across sections, matching the page.
 * No pricing anywhere: the file is a pure materials takeoff (per product
 * decision, Jul 2026).
 */

import { EstimateOutput } from '../types';
import { getCustomFieldConfig, getCustomFieldsForProduct, getOutputLayout } from '../data/productTable';
import { buildBomTable, pickLayout, RenderCell, RenderCtx } from './renderModel';
import { bomSections } from '../data/bomSections';
import { tr } from '../i18n/standalone';

/** Estimate-level context rows for the top of the file. All optional — a row
 *  is emitted only when its value is present.
 *
 *  Display-ready values: the caller resolves project type, space type, colors
 *  and orientation into the active language before handing them over, so this
 *  module only has to translate its own labels. */
export interface CsvMetadata {
  projectTitle?: string;
  estimateUrl?: string;
  projectType?: string;
  spaceType?: string;
  roomLengthFt?: number;
  roomWidthFt?: number;
  wallHeightFt?: number;
  wallColor?: string;
  wallOrientation?: string;
  ceilingIncluded?: boolean;
  ceilingColor?: string;
  garageDoors?: number;
}

/** RFC-4180 escaping; CRLF row endings are applied at join time. */
const escapeCsv = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const cellText = (cell: RenderCell): string => {
  switch (cell.type) {
    case 'text':
    case 'link':
      return cell.text;
    case 'barcode':
      return cell.alt; // the encoded value (UPC etc.) — the image is meaningless in a CSV
    default:
      return ''; // checkbox / empty
  }
};

export function estimateToCsv(estimate: EstimateOutput, meta: CsvMetadata): string {
  // Prices are structurally excluded: with showPrices=false the render model
  // never creates the Unit Price / Total columns.
  const ctx: RenderCtx = {
    currency: estimate.currency,
    showPrices: false,
    getFields: getCustomFieldsForProduct,
  };
  const fieldConfig = getCustomFieldConfig();
  const bomLayout = pickLayout(getOutputLayout(), 'bom');

  const sections = bomSections(estimate.billOfMaterials).filter((s) => s.items.length > 0);
  const anyPanel = sections.some((s) => s.isPanel);

  // Master column set: an empty build with the panel flag of the widest
  // section shape. Section column sets differ only by the panel-only Total
  // Sqft column, so the master is a superset; rows resolve per-section by
  // layoutId and leave unmatched cells blank (e.g. trim rows' Total Sqft).
  // The # column is pinned first: on the page # sits relative to the
  // checkbox, which the file doesn't have.
  const built = buildBomTable([], anyPanel, ctx, fieldConfig, bomLayout, 1).columns
    .filter((c) => c.visibleBrowser && c.kind !== 'checkbox');
  const master = [
    ...built.filter((c) => c.kind === 'lineNo'),
    ...built.filter((c) => c.kind !== 'lineNo'),
  ];

  const dataRow = (cols: string[]): string => cols.map(escapeCsv).join(',');

  const lines: string[] = [];

  // ── Metadata block ──────────────────────────────────────────────────────
  // Color and orientation arrive already translated (the caller resolves them
  // through configDataPoints), so they are written through as-is.
  lines.push(tr('results:csv.fileTitle'));
  const metaRows: Array<[string, string | undefined]> = [
    [tr('results:csv.projectTitle'), meta.projectTitle],
    [tr('results:csv.generated'), new Date().toISOString().slice(0, 10)],
    [tr('results:csv.estimateUrl'), meta.estimateUrl],
    [tr('results:config.projectType'), meta.projectType],
    [tr('wizard:shared.spaceType'), meta.spaceType],
    [tr('results:csv.roomLength'), meta.roomLengthFt !== undefined ? String(meta.roomLengthFt) : undefined],
    [tr('results:csv.roomWidth'), meta.roomWidthFt !== undefined ? String(meta.roomWidthFt) : undefined],
    [tr('results:csv.wallHeight'), meta.wallHeightFt !== undefined ? String(meta.wallHeightFt) : undefined],
    [tr('results:config.wallColor'), meta.wallColor],
    [tr('results:config.panelOrientation'), meta.wallOrientation],
    [tr('results:csv.ceilingIncluded'), meta.ceilingIncluded === undefined
      ? undefined
      : tr(meta.ceilingIncluded ? 'wizard:shared.yes' : 'wizard:shared.no')],
    [tr('results:csv.ceilingColor'), meta.ceilingColor],
    [tr('results:config.garageDoors'), meta.garageDoors !== undefined ? String(meta.garageDoors) : undefined],
  ];
  metaRows.forEach(([label, value]) => {
    if (value !== undefined && value !== '') lines.push(dataRow([label, value]));
  });
  lines.push('');

  // ── Flat materials table ────────────────────────────────────────────────
  lines.push(dataRow(master.map((c) => c.heading)));

  let lineNo = 1;
  const totals = { qty: 0, linearFt: 0, sqft: 0 };

  sections.forEach(({ items, isPanel }) => {
    const model = buildBomTable(items, isPanel, ctx, fieldConfig, bomLayout, lineNo);
    const byLayoutId = new Map(model.columns.map((c) => [c.layoutId, c]));
    model.rows.forEach((row) => {
      const cells = master.map((mc) => {
        const col = byLayoutId.get(mc.layoutId);
        return col ? cellText(col.cell(row, ctx)) : '';
      });
      lines.push(dataRow(cells));
      totals.qty += row.quantity;
      totals.linearFt += row.lengthFeet * row.quantity;
      if (isPanel) totals.sqft += row.sqft * row.quantity;
    });
    lineNo += items.length;
  });

  // Grand-total row (same arithmetic as the per-section footers); the label
  // sits in the Product column, mirroring the on-screen footers.
  lines.push(dataRow(master.map((mc): string => {
    switch (mc.kind) {
      case 'product': return tr('results:csv.grandTotal');
      case 'qty': return String(totals.qty);
      case 'linearFt': return totals.linearFt.toLocaleString();
      case 'totalSqft': return totals.sqft.toLocaleString(undefined, { maximumFractionDigits: 0 });
      default: return '';
    }
  })));
  lines.push('');

  // Screws are a separate unnumbered block: on screen they aren't a BOM table
  // row, and numbering them would break page↔file line-number agreement.
  lines.push(dataRow(master.map((mc): string => {
    switch (mc.kind) {
      case 'product': return tr('results:bom.hardware.totalScrews');
      case 'qty': return estimate.billOfMaterials.screws.total.toLocaleString();
      default: return '';
    }
  })));

  return lines.join('\r\n') + '\r\n';
}

const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

/** Build the CSV and trigger a browser download (same in-browser save pattern
 *  as the PDF path). UTF-8 BOM so Excel decodes the file correctly. */
export function downloadEstimateCsv(estimate: EstimateOutput, meta: CsvMetadata): void {
  const csv = estimateToCsv(estimate, meta);
  const named = meta.projectTitle && slugify(meta.projectTitle);
  const filename = `${named || `trusscore-estimate-${new Date().toISOString().slice(0, 10)}`}.csv`;

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
